// The one registry of `data-tour` anchors shared by the Academy's scripted
// scenes (the living player) and the guided tours (react-joyride). Scenes and
// tours import these constants instead of writing selector strings, so a
// renamed attribute fails one test (`selectors.test.ts`) instead of silently
// breaking a lesson. `file` is where the attribute lives, relative to
// client/src, so the test can assert the literal is still there.
export interface TourAnchor {
  /** The `data-tour` attribute value. */
  id: string;
  /** Source file (relative to client/src) that renders the attribute. */
  file: string;
}

export const ANCHORS = {
  // Agency portal (Agency/Dashboard.tsx, Officers tab)
  agencyTrialBanner: { id: "agency-trial-banner", file: "components/Agency/Dashboard.tsx" },
  agencyInviteCard: { id: "agency-invite-card", file: "components/Agency/OfficerInvite.tsx" },
  agencyInviteEmails: { id: "agency-invite-emails", file: "components/Agency/EmailInviteInput.tsx" },
  agencyInviteSend: { id: "agency-invite-send", file: "components/Agency/EmailInviteInput.tsx" },
  agencyOfficerTable: { id: "agency-officer-table", file: "components/Agency/OfficerTable.tsx" },
  agencyInvestigatorSeats: { id: "agency-investigator-seats", file: "components/Agency/Dashboard.tsx" },
  agencyTourReplay: { id: "agency-tour-replay", file: "components/Agency/Dashboard.tsx" },

  // Sidebar
  newCaseDropdown: { id: "new-case-dropdown", file: "components/Menu/Menu.tsx" },
  newCaseRecord: { id: "new-case-record", file: "components/common/NewCaseDropdown.tsx" },
  newCaseUpload: { id: "new-case-upload", file: "components/common/NewCaseDropdown.tsx" },
  newCaseBlank: { id: "new-case-blank", file: "components/common/NewCaseDropdown.tsx" },
  casesNav: { id: "cases-nav", file: "components/Menu/Menu.tsx" },
  academyNav: { id: "academy-nav", file: "components/Menu/Menu.tsx" },
  filesNav: { id: "files-nav", file: "components/Menu/Menu.tsx" },
  formsNav: { id: "forms-nav", file: "components/Menu/Menu.tsx" },
  formsUploadButton: { id: "forms-upload-button", file: "components/Forms/FormTemplateManager.tsx" },
  formUploadDrop: { id: "form-upload-drop", file: "components/Forms/FormTemplateUpload.tsx" },
  formUploadDetected: { id: "form-upload-detected", file: "components/Forms/FormTemplateUpload.tsx" },
  formUploadName: { id: "form-upload-name", file: "components/Forms/FormTemplateUpload.tsx" },
  formUploadDescription: { id: "form-upload-description", file: "components/Forms/FormTemplateUpload.tsx" },
  formUploadScope: { id: "form-upload-scope", file: "components/Forms/FormTemplateUpload.tsx" },
  formUploadSubmit: { id: "form-upload-submit", file: "components/Forms/FormTemplateUpload.tsx" },
  uploadTray: { id: "upload-tray", file: "components/common/UploadTrayView.tsx" },
  formTemplateName: { id: "form-template-name", file: "components/Forms/FormTemplateManager.tsx" },
  formTemplateStats: { id: "form-template-stats", file: "components/Forms/FormTemplateManager.tsx" },
  formEditFields: { id: "form-edit-fields", file: "components/Forms/FormTemplateManager.tsx" },
  redactionsNav: { id: "redactions-nav", file: "components/Menu/Menu.tsx" },
  casesSearch: { id: "cases-search", file: "components/Cases/Cases.tsx" },
  uploadPrevious: { id: "upload-previous", file: "components/Menu/UploadModal.tsx" },
  uploadCompressor: { id: "upload-compressor", file: "components/Menu/UploadModal.tsx" },
  cancelUpload: { id: "cancel-upload", file: "components/common/UploadProgressToast.tsx" },
  settingsButton: { id: "settings-button", file: "components/Menu/Menu.tsx" },
  caseCategories: { id: "case-categories", file: "components/Menu/CaseCategories.tsx" },
  newCategoryButton: { id: "new-category-button", file: "components/Menu/CaseCategories.tsx" },
  favoritesCategory: { id: "favorites-category", file: "components/Menu/CaseCategories.tsx" },
  categoryRow: { id: "category-row", file: "components/Menu/CaseCategories.tsx" },
  categoryMenuButton: { id: "category-menu-button", file: "components/Menu/CaseCategories.tsx" },
  categoryMenu: { id: "category-menu", file: "components/Menu/CaseCategories.tsx" },
  categoryDialog: { id: "category-dialog", file: "components/Menu/CaseCategories.tsx" },
  categoryDeleteDialog: { id: "category-delete-dialog", file: "components/Menu/CaseCategories.tsx" },
  categoryTagMenu: { id: "category-tag-menu", file: "components/Editor/CaseCategoryMenu.tsx" },
  trashNav: { id: "trash-nav", file: "components/Menu/Menu.tsx" },
  deleteCase: { id: "delete-case", file: "components/Cases/Cases.tsx" },
  trashBanner: { id: "trash-banner", file: "components/Cases/Cases.tsx" },
  editorRestore: { id: "editor-restore", file: "components/Editor/EditorMenuBar.tsx" },

  // Upload Media Files modal
  uploadModal: { id: "upload-modal", file: "components/Menu/UploadModal.tsx" },
  uploadDropzone: { id: "upload-dropzone", file: "components/Menu/UploadModal.tsx" },
  uploadFileRow: { id: "upload-file-row", file: "components/Menu/UploadModal.tsx" },
  uploadTypeNarrative: { id: "upload-type-narrative", file: "components/Menu/UploadModal.tsx" },
  uploadTypeInteraction: { id: "upload-type-interaction", file: "components/Menu/UploadModal.tsx" },
  uploadReportSettings: { id: "upload-report-settings", file: "components/Menu/UploadModal.tsx" },
  uploadSubmit: { id: "upload-submit", file: "components/Menu/UploadModal.tsx" },
  uploadBodycam: { id: "upload-bodycam", file: "components/Menu/UploadModal.tsx" },
  evidenceModal: { id: "evidence-modal", file: "components/Menu/EvidenceModal.tsx" },
  evidenceList: { id: "evidence-list", file: "components/Menu/EvidenceModal.tsx" },
  evidenceAdd: { id: "evidence-add", file: "components/Menu/EvidenceModal.tsx" },
  uploadToast: { id: "upload-toast", file: "components/common/UploadProgressToast.tsx" },
  uploadAddSpeaker: { id: "upload-add-speaker", file: "components/Menu/UploadModal.tsx" },
  uploadSpeakerInformation: { id: "upload-speaker-information", file: "components/Menu/UploadModal.tsx" },
  uploadSpokenLanguages: { id: "upload-spoken-languages", file: "components/Menu/UploadModal.tsx" },
  uploadVideoAnalysis: { id: "upload-video-analysis", file: "components/Menu/UploadModal.tsx" },
  uploadKeepVideo: { id: "upload-keep-video", file: "components/Menu/UploadModal.tsx" },
  uploadVideoVerify: { id: "upload-video-verify", file: "components/Menu/UploadModal.tsx" },
  uploadVideoEnrich: { id: "upload-video-enrich", file: "components/Menu/UploadModal.tsx" },

  // Footage tab (shared video view, components/common/video)
  footageVideo: { id: "footage-video", file: "components/common/video/VideoPlayer.tsx" },
  footageSeekbar: { id: "footage-seekbar", file: "components/common/video/VideoPlayer.tsx" },
  footagePointNav: { id: "footage-point-nav", file: "components/common/video/VideoPlayer.tsx" },
  footageNextPoint: { id: "footage-next-point", file: "components/common/video/VideoPlayer.tsx" },
  footageMarker: { id: "footage-marker", file: "components/common/video/EventMarkerBar.tsx" },
  footagePoints: { id: "footage-points", file: "components/common/video/PointsOfInterestList.tsx" },
  footagePoint: { id: "footage-point", file: "components/common/video/PointsOfInterestList.tsx" },
  footageKindFilter: { id: "footage-kind-filter", file: "components/common/video/PointsOfInterestList.tsx" },
  footageTranscript: { id: "footage-transcript", file: "components/common/video/VideoView.tsx" },
  footageAside: { id: "footage-aside", file: "components/Editor/FootagePanel.tsx" },
  footageCheck: { id: "footage-check", file: "components/Editor/FootagePanel.tsx" },
  footageAttach: { id: "footage-attach", file: "components/Editor/FootagePanel.tsx" },
  footageStill: { id: "footage-still", file: "components/Editor/FootagePanel.tsx" },

  // Case editor
  editorProcessing: { id: "editor-processing", file: "components/Editor.tsx" },
  editorNarrative: { id: "editor-narrative", file: "components/Editor.tsx" },
  editorExport: { id: "editor-export", file: "components/Editor/EditorMenuBar.tsx" },
  reportScore: { id: "report-score", file: "components/Editor/ReportScore.tsx" },
  suggestionItem: { id: "suggestion-item", file: "components/Editor/Suggestions.tsx" },
  addToReportButton: { id: "add-to-report-button", file: "components/Editor/Suggestions.tsx" },
  reEvaluate: { id: "re-evaluate", file: "components/Editor/Suggestions.tsx" },
  hidePlaceholders: { id: "hide-placeholders", file: "components/Editor/Suggestions.tsx" },
  caseListItem: { id: "case-list-item", file: "components/Cases/Case.tsx" },
  favoriteStar: { id: "favorite-star", file: "components/Editor/EditorMenuBar.tsx" },
  caseCategoryTag: { id: "case-category-tag", file: "components/Editor/CaseCategoryMenu.tsx" },
  editorToolbar: { id: "editor-toolbar", file: "components/Editor/EditorMenuBar.tsx" },
  editorStyles: { id: "editor-styles", file: "components/Editor/EditorMenuBar.tsx" },
  editorTranscribe: { id: "editor-transcribe", file: "components/Editor/EditorMenuBar.tsx" },
  editorFind: { id: "editor-find", file: "components/Editor/EditorMenuBar.tsx" },
  editorProofread: { id: "editor-proofread", file: "components/Editor/EditorMenuBar.tsx" },
  replaceAll: { id: "replace-all", file: "components/Editor/EditorMenuBar.tsx" },
  findBar: { id: "find-bar", file: "components/Editor/FindBar.tsx" },
  findReplaceToggle: { id: "find-replace-toggle", file: "components/Editor/FindBar.tsx" },
  editorTabNarrative: { id: "editor-tab-narrative", file: "components/Editor.tsx" },
  editorTabTranscription: { id: "editor-tab-transcription", file: "components/Editor.tsx" },
  editorSidebarTabs: { id: "editor-sidebar-tabs", file: "components/Editor.tsx" },
  enhanceWriting: { id: "enhance-writing", file: "components/Editor/FloatingMenu.tsx" },
  enhanceCompare: { id: "enhance-compare", file: "components/EnhanceCompareModal.tsx" },
  proofPopover: { id: "proof-popover", file: "components/Editor/ProofreadPopover.tsx" },
  proofApply: { id: "proof-apply", file: "components/Editor/ProofreadPopover.tsx" },
  proofDictionary: { id: "proof-dictionary", file: "components/Editor/ProofreadPopover.tsx" },
  enhanceAccept: { id: "enhance-accept", file: "components/EnhanceCompareModal.tsx" },

  // Add by voice: the review card for Address Suggestions and the dictation menu
  policyBadge: { id: "policy-badge", file: "components/Editor/SuggestionTagBadges.tsx" },
  policySection: { id: "policy-section", file: "components/Editor/PolicySectionView.tsx" },

  // Policies page, document viewer, and chat (search-and-ask)
  policiesNav: { id: "policies-nav", file: "components/Menu/Menu.tsx" },
  policiesTabDocuments: { id: "policies-tab-documents", file: "components/Policies/PoliciesHome.tsx" },
  policiesTabChat: { id: "policies-tab-chat", file: "components/Policies/PoliciesHome.tsx" },
  policiesAsk: { id: "policies-ask", file: "components/Policies/PoliciesHome.tsx" },
  policiesSearch: { id: "policies-search", file: "components/Policies/tabs/PolicyDocumentsTab.tsx" },
  policyDocRow: { id: "policy-doc-row", file: "components/Policies/tabs/PolicyDocumentsTab.tsx" },
  policyBack: { id: "policy-back", file: "components/Policies/PolicyDocumentDetail.tsx" },
  policyDownload: { id: "policy-download", file: "components/Policies/PolicyDocumentDetail.tsx" },
  policyOutlineFilter: { id: "policy-outline-filter", file: "components/Policies/PolicyDocumentDetail.tsx" },
  policyOutlineRow: { id: "policy-outline-row", file: "components/Policies/PolicyDocumentDetail.tsx" },
  chatSuggestion: { id: "chat-suggestion", file: "components/common/chat/ChatPanel.tsx" },
  chatComposer: { id: "chat-composer", file: "components/common/chat/ChatPanel.tsx" },
  chatAnswer: { id: "chat-answer", file: "components/common/chat/ChatPanel.tsx" },
  chatCitation: { id: "chat-citation", file: "components/common/chat/ChatPanel.tsx" },
  suggestionsReview: { id: "suggestions-review", file: "components/Editor/SuggestionsReview.tsx" },
  reviewAccept: { id: "review-accept", file: "components/Editor/SuggestionsReview.tsx" },
  reviewDiscard: { id: "review-discard", file: "components/Editor/SuggestionsReview.tsx" },
  dictationEnhanced: { id: "dictation-enhanced", file: "components/Editor/EditorMenuBar.tsx" },
  dictationPure: { id: "dictation-pure", file: "components/Editor/EditorMenuBar.tsx" },

  // Regenerate (Editor narrative dropdown + confirm dialog)
  regenerateNarrative: { id: "regenerate-narrative", file: "components/Editor.tsx" },
  regenerateDialog: { id: "regenerate-dialog", file: "components/Editor.tsx" },
  regenerateConfirm: { id: "regenerate-confirm", file: "components/Editor.tsx" },

  // Transcription tab
  transcriptFollow: { id: "transcript-follow", file: "components/Editor.tsx" },
  transcriptSegment: { id: "transcript-segment", file: "components/Editor/Transcription.tsx" },
  transcriptSpeaker: { id: "transcript-speaker", file: "components/Editor/Transcription.tsx" },
  transcriptSpeakerEdit: { id: "transcript-speaker-edit", file: "components/Editor/Transcription.tsx" },
  transcriptSpeakerDialog: { id: "transcript-speaker-dialog", file: "components/Editor/Transcription.tsx" },
  transcriptSpeakerSave: { id: "transcript-speaker-save", file: "components/Editor/Transcription.tsx" },
  transcriptSegmentMenu: { id: "transcript-segment-menu", file: "components/Editor/Transcription.tsx" },
  transcriptSegmentEdit: { id: "transcript-segment-edit", file: "components/Editor/Transcription.tsx" },
  transcriptSegmentSave: { id: "transcript-segment-save", file: "components/Editor/Transcription.tsx" },
  transcriptSegmentSplit: { id: "transcript-segment-split", file: "components/Editor/Transcription.tsx" },
  transcriptSegmentMergeNext: { id: "transcript-segment-merge-next", file: "components/Editor/Transcription.tsx" },
  transcriptSplitPoint: { id: "transcript-split-point", file: "components/Editor/Transcription.tsx" },

  // Export dialogs (EditorMenuBar)
  exportClipboardDialog: { id: "export-clipboard-dialog", file: "components/Editor/EditorMenuBar.tsx" },
  exportEmailDialog: { id: "export-email-dialog", file: "components/Editor/EditorMenuBar.tsx" },
  exportWordDialog: { id: "export-word-dialog", file: "components/Editor/EditorMenuBar.tsx" },
  exportPdfDialog: { id: "export-pdf-dialog", file: "components/Editor/EditorMenuBar.tsx" },
  exportTranscriptionDialog: { id: "export-transcription-dialog", file: "components/Editor/EditorMenuBar.tsx" },

  // Report Preferences page
  preferencesButton: { id: "preferences-button", file: "components/Menu/Menu.tsx" },
  preferencesForm: { id: "preferences-form", file: "components/Settings/PreferencesForm.tsx" },
  preferenceField: { id: "preference-field", file: "components/Settings/PreferencesForm.tsx" },
  preferencesSave: { id: "preferences-save", file: "components/Settings/PreferencesForm.tsx" },

  // Report templates (Report Preferences page) and the upload modal's override fields
  templateManager: { id: "template-manager", file: "components/Settings/ReportTemplates/ReportTemplateManager.tsx" },
  templateNew: { id: "template-new", file: "components/Settings/ReportTemplates/ReportTemplateManager.tsx" },
  templateDetails: { id: "template-details", file: "components/Settings/ReportTemplates/ReportTemplateManager.tsx" },
  templateActions: { id: "template-actions", file: "components/Settings/ReportTemplates/ReportTemplateManager.tsx" },
  templateHeader: { id: "template-header", file: "components/Settings/ReportTemplates/ReportTemplateEditor.tsx" },
  templateBody: { id: "template-body", file: "components/Settings/ReportTemplates/ReportTemplateEditor.tsx" },
  templateFooter: { id: "template-footer", file: "components/Settings/ReportTemplates/ReportTemplateEditor.tsx" },
  templateInsert: { id: "template-insert", file: "components/Settings/ReportTemplates/ReportTemplateEditor.tsx" },
  uploadTemplate: { id: "upload-template", file: "components/Menu/UploadModal.tsx" },
  uploadCallSign: { id: "upload-call-sign", file: "components/Menu/UploadModal.tsx" },

  // Editor sidebar: Statutes and Snippets tabs
  statutesPanel: { id: "statutes-panel", file: "components/Editor/Statutes.tsx" },
  statuteItem: { id: "statute-item", file: "components/Editor/Statutes.tsx" },
  statuteIdentify: { id: "statute-identify", file: "components/Editor/Statutes.tsx" },
  snippetsPanel: { id: "snippets-panel", file: "components/Editor/Snippets.tsx" },
  snippetItem: { id: "snippet-item", file: "components/Editor/Snippets.tsx" },
  snippetAdd: { id: "snippet-add", file: "components/Editor/Snippets.tsx" },
  snippetDialog: { id: "snippet-dialog", file: "components/Editor/Snippets.tsx" },
  snippetSave: { id: "snippet-save", file: "components/Editor/Snippets.tsx" },
} as const satisfies Record<string, TourAnchor>;

export type AnchorKey = keyof typeof ANCHORS;

/** CSS selector for an anchor: `[data-tour="…"]`. */
export const sel = (key: AnchorKey): string => `[data-tour="${ANCHORS[key].id}"]`;

/** The `data-tour` attribute value for an anchor (for JSX). */
export const tourId = (key: AnchorKey): string => ANCHORS[key].id;
